<template>
  <div class="pb-2">
    <div
      class="card-servicios__container bg-primary text-white position-relative"
      :class="{ 'collapsed-header': !openDetails && agrupacionesServicios.length > 0 }">
      <button
        v-if="isBorrable"
        class="btn btn-primary bg-transparent border-0 position-absolute top-0 end-0 mt-2 me-2 action-button d-flex align-items-center justify-content-center text-white"
        @click="cerrarCardServicio()">
        <i class="fa-solid fa-xmark icon-centered d-flex align-items-center justify-content-center" />
      </button>

      <div class="card-servicios__wrapper d-flex align-items-center">
        <div class="card-servicios__content flex-grow-1 d-flex flex-column">
          <div class="card-servicios__info d-flex flex-column">
            <span class="mb-10 fs-16 fw-semibold">
              {{ $t('general.persona') }} {{ numeroReserva }}
              <span v-if="!existeCodigoUnirse && numeroPlazas !== null">/ {{ numeroPlazas }}</span>
            </span>
            <template v-if="hasModificarPersona">
              <div v-if="isPrimeraReserva" class="form-check checkbox-container d-flex align-items-center">
                <input
                  :id="'checkboxSoyYo' + numeroReserva"
                  v-model="reserva.reservaPropia"
                  type="checkbox"
                  class="form-check-input me-2 custom-checkbox" />
                <label :for="'checkboxSoyYo' + numeroReserva" class="form-check-label fs-13 text-white mb-0">
                  {{ $t('general.esMiReserva') }}
                </label>
              </div>
            </template>
          </div>

          <div v-if="!reserva.reservaPropia && hasModificarPersona" class="card-servicios__inputs-row mt-2">
            <!-- Desktop -->
            <div class="row d-none d-md-flex">
              <div class="col-6 pe-2">
                <input
                  v-model="reserva.usuario.nombre"
                  type="text"
                  :placeholder="$t('input.field.nombre')"
                  class="form-control text-start placeholder-start fs-16 custom-input-height" />
              </div>
              <div class="col-6 ps-2">
                <input
                  v-model="reserva.usuario.apellido"
                  type="text"
                  :placeholder="$t('input.field.apellido')"
                  class="form-control text-start placeholder-start fs-16 custom-input-height" />
              </div>
            </div>

            <!-- Mobile -->
            <div class="d-md-none">
              <div class="mb-2">
                <input
                  v-model="reserva.usuario.nombre"
                  type="text"
                  :placeholder="$t('input.field.nombre')"
                  class="form-control text-start placeholder-start fs-16 custom-input-height" />
              </div>
              <div>
                <input
                  v-model="reserva.usuario.apellido"
                  type="text"
                  :placeholder="$t('input.field.apellido')"
                  class="form-control text-start placeholder-start fs-16 custom-input-height" />
              </div>
            </div>
          </div>

          <div v-else-if="reserva.reservaPropia && reserva.usuario.nombre.length > 0" class="mt-15">
            <div class="text-white fs-19 fw-semibold">{{ reserva.usuario.nombre }} {{ reserva.usuario.apellido }}</div>
          </div>

          <div v-if="hasModificarPersona && !reserva.reservaPropia" class="mt-2">
            <div class="form-check checkbox-container d-flex align-items-center">
              <input
                :id="'checkMenorEdad' + numeroReserva"
                v-model="menor"
                type="checkbox"
                class="form-check-input me-2 custom-checkbox"
                @change="emits('isMenorChanged', menor)" />
              <label :for="'checkMenorEdad' + numeroReserva" class="form-check-label fs-13 text-white mb-0">
                {{ $t('general.menorEdad') }}
              </label>
            </div>
          </div>
          <div v-if="fechaMenorEdad" class="row">
            <div class="calendar-usuario__container mt-3 col-12 col-sm-6 col-md-4">
              <label class="text-white mb-2 d-block">{{ $t('home.form.personalInformation.birthDate') }}</label>
              <Datepicker
                v-model="reserva.usuario.fecha"
                :format="format"
                :flow="['year', 'month', 'calendar']"
                auto-apply />
            </div>
          </div>
        </div>

        <div class="card-servicios__actions flex-shrink-0 d-flex align-items-center justify-content-center">
          <button
            v-if="agrupacionesServicios.length > 0"
            class="btn btn-primary bg-transparent border-0 action-button d-flex align-items-center justify-content-center text-white"
            @click="toggleOpenDetails">
            <CustomIcon :icon="openDetails ? arrowUpIcon : arrowDownIcon" size="24px" />
          </button>
        </div>
      </div>
    </div>
    <div
      v-if="agrupacionesServicios.length > 0"
      class="card mx-0 p-15 selector-servicios"
      :class="[openDetails ? 'd-block' : 'd-none', !openDetails ? 'collapsed-card' : '']"
      @click="emits('habilitarModificar')">
      <template v-for="agrupacionServicios in agrupacionesServicios" :key="agrupacionServicios.id">
        <div class="row">
          <ReservaServiciosSeleccion :agrupacion-servicios="agrupacionServicios" :idx-reserva="idxReserva" />
        </div>
      </template>
    </div>
  </div>
</template>

<script setup>
  import { ref, watch, computed, onMounted } from 'vue';
  import { useRoute } from 'vue-router';
  import { informacionHabitacion } from '@/store_pinia/informacionHabitacion';
  import ReservaServiciosSeleccion from './ReservaServiciosSeleccion.vue';
  import Datepicker from '@vuepic/vue-datepicker';
  import { storeToRefs } from 'pinia/dist/pinia';
  import moment from 'moment';
  import { useUserStore } from '@/store_pinia/userStore';
  import CustomIcon from '@/components/CustomIcon.vue';
  import arrowDownIcon from '@/assets/icons/arrow_down.svg';
  import arrowUpIcon from '@/assets/icons/arrow_up.svg';

  const props = defineProps({
    idxReserva: {
      type: Number,
      default: null,
    },
    numeroPlazas: {
      type: Number,
      default: null,
    },
    agrupacionesServicios: {
      type: Array, // VGAgrupacionServiciosDTO[]
      default: () => [],
    },
    hasModificarPersona: {
      type: Boolean,
      default: () => true,
    },
    openDetails: {
      type: Boolean,
      default: true,
    },
  });

  const emits = defineEmits([
    'habilitarModificar',
    'close',
    'toggleOpenDetails',
    'servicioSeleccionado',
    'isMenorChanged',
  ]);

  // todo -> remove this, a component should be route agnostic. All needed should be pass as parameter
  const route = useRoute();
  const userStore = useUserStore();
  const storeHabitacion = informacionHabitacion();
  const { reservas } = storeToRefs(storeHabitacion);

  const reserva = computed(() => reservas.value[props.idxReserva]);
  const menor = ref(false);

  const numeroReserva = computed(() => props.idxReserva + 1);
  const fechaMenorEdad = computed(() => menor.value && !reserva.value.reservaPropia);
  const isPrimeraReserva = computed(() => numeroReserva.value === 1);
  const existeCodigoUnirse = route.query.codigo;
  const isBorrable = computed(() => !isPrimeraReserva.value);

  function refreshUsuario(forceChange) {
    if (!reserva.value.reservaPropia && forceChange) {
      reserva.value.usuario.nombre = '';
      reserva.value.usuario.apellido = '';
    } else if (reserva.value.reservaPropia && userStore.isLogged) {
      reserva.value.usuario.nombre = userStore.getUsuario.nombre;
      reserva.value.usuario.apellido = userStore.getUsuario.apellidos.split(' ')[0];
    }
  }

  function format(fecha) {
    return moment(fecha).format('DD-MM-YYYY');
  }

  function cerrarCardServicio() {
    emits('close');
  }

  function toggleOpenDetails() {
    emits('toggleOpenDetails');
  }

  watch(
    () => reserva.value.reservaPropia,
    () => {
      refreshUsuario(true);
    }
  );

  watch(userStore.isLogged, () => {
    refreshUsuario(false);
  });

  onMounted(() => {
    refreshUsuario(false);
  });
</script>

<style lang="scss" scoped>
  .text-primary {
    @media (max-width: 768px) {
      font-size: 13px;
    }
    font-size: 13px;
  }

  .v-enter-active,
  .v-leave-active {
    transition: opacity 0.3s ease;
  }

  .v-enter-from,
  .v-leave-to {
    opacity: 0;
  }

  .form-check-input:checked {
    background-color: var(--bs-secondary);
    border-color: var(--bs-secondary);
  }

  .form-check-input {
    border-radius: 2px;
  }

  .action-button {
    padding: 8px;
    box-shadow: none !important;
    border: none !important;

    &:focus,
    &:active,
    &:hover {
      box-shadow: none !important;
      border: none !important;
    }
  }

  .icon-centered {
    width: 24px;
    height: 24px;
    font-size: 16px;
  }

  .card-servicios {
    &__container {
      padding: 15px 20px;
      border-top-left-radius: var(--bs-border-radius-xl) !important;
      border-top-right-radius: var(--bs-border-radius-xl) !important;
      background: var(--bs-primary);

      @media (max-width: 768px) {
        font-size: 13px;
      }

      &.collapsed-header {
        border-bottom-left-radius: var(--bs-border-radius-xl) !important;
        border-bottom-right-radius: var(--bs-border-radius-xl) !important;
      }
    }

    &__wrapper {
      min-height: 60px;
    }

    &__actions {
      margin-left: 15px;
    }

    .custom-checkbox {
      width: 15px !important;
      height: 15px !important;
      border-radius: 2px !important;
      margin-top: 0 !important;
      margin-bottom: 0 !important;
      flex-shrink: 0;
    }

    .checkbox-container {
      min-height: auto;

      .form-check-input {
        margin-top: 0 !important;
        vertical-align: middle;
      }

      .form-check-label {
        line-height: 15px !important;
        padding-top: 0 !important;
        margin-top: 0 !important;
        margin-bottom: 0 !important;
        vertical-align: middle;
        display: inline-block;
      }
    }
  }

  .selector-servicios {
    border-top-left-radius: 0;
    border-top-right-radius: 0;
    border-bottom-left-radius: var(--bs-border-radius-xl);
    border-bottom-right-radius: var(--bs-border-radius-xl);

    @media (max-width: 768px) {
      padding: 10px;
    }
  }

  .calendar-usuario__container {
    label {
      font-size: 14px;
    }
  }

  .placeholder-start::placeholder {
    text-align: left !important;
  }

  .custom-input-height {
    height: 35px !important;
  }
</style>
